import { Box, Divider, Grid2, Paper, styled, Typography } from "@mui/material";
import React, { useEffect, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import Lottie from "react-lottie";
import PurpleBlinkingLottie from '../../../../Components/Lotties/PurpleBlinkingLottie.json';
import BatteryOverviewIndicator from "./BatteryOverviewIndicator";
import BatteryOverviewKeyMetric from "./BatteryOverviewKeyMetric";
import BatteryOverviewStringv2 from "./BatteryOverviewStringv2";
import { useBatteryInfo } from "../../../../Pages/DigitalTwinV3/DigitalTwinContext";
import { getBatteryIndicators, getBatteryStrings } from "../../../../Api/DigitalTwin";
import { usePagesContent } from "../../../../Hooks/usePagesContent";
import { getDigitalTwinList } from "../../../../Api/DigitalTwinListApi";

const OverviewContainer = styled(Box)(({ theme }) => ({
    marginTop: '20px',
    width: '100%',
    minHeight: '200px',
}))

export default function BatteryOverview() {

    const { t } = useTranslation(['common', 'digital_twin']);
    const { batteryID } = useBatteryInfo();
    const [indicatorsData, setIndicatorsData] = useState({});
    const [keyMetricsData, setKeyMetricsData] = useState({});
    const [stringsData, setStringsData] = useState([]);
    const { metaInfo: pageMetaInfo = {} } = usePagesContent();

    const lottieOptions = {
        loop: true,
        autoplay: true,
        animationData: PurpleBlinkingLottie,
        rendererSettings: {
            preserveAspectRatio: "xMidYMid slice"
        }
    };

    const fetchBatteryOverviewData = useCallback(async () => {
        try {
            const [indicatorsRes, keyMetricsRes, stringsRes] = await Promise.all([
                getBatteryIndicators(batteryID, pageMetaInfo.indicatorProfile),
                getDigitalTwinList({ pageNumber: 1, rowsPerPage: 1, filters: { batteryID: [batteryID] } }),
                getBatteryStrings(batteryID),
            ]);
            setIndicatorsData(indicatorsRes.response.data ?? {});
            setKeyMetricsData(keyMetricsRes.response.data[0] ?? {});
            setStringsData(stringsRes.response.data ?? []);
        } catch (error) {
            console.error('Error fetching digital twin hierarchy:', error);
        }
    }, [batteryID, pageMetaInfo.indicatorProfile]);

    useEffect(() => {
        fetchBatteryOverviewData();
    }, [fetchBatteryOverviewData]);

    return (
        <>
            <Grid2 container flexDirection="column" gap="4px" paddingTop={'20px'} paddingLeft={'12px'}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Box>
                        <Lottie
                            options={lottieOptions}
                            height={20}
                            width={20}
                        />
                    </Box>
                    <Typography variant="fw500s16lh20c000000">{t("digital_twin:overview_tab.title")}</Typography>
                </Box>
                <Typography variant="fw400s12c686868">{t("digital_twin:overview_tab.subtitle")}</Typography>
            </Grid2>
            <Grid2 sx={{ mb: 10 }}>
                <OverviewContainer>
                    <Paper elevation={0}
                        sx={{
                            px: 5,
                            py: 4,
                            height: "100%",
                            borderRadius: "8px",
                            border: "1px solid #E6E6E6",
                        }}
                    >
                        <Grid2 container flexDirection="column" gap="16px">
                            <Typography variant="fw500s16lh24c000000">{t("digital_twin:indicators")}</Typography>
                            <Grid2 container gap="8px">
                                {(pageMetaInfo.batteryIndicators ?? []).map((indicator) => (
                                    <BatteryOverviewIndicator name={indicator.label} isOn={(indicatorsData[indicator.key] ?? '').toLowerCase() === 'on'} />
                                ))}
                            </Grid2>
                        </Grid2>
                        <Divider sx={{ my: 2, borderColor: '#E9E9E9' }} />
                        <Grid2 container flexDirection="column" gap="16px" sx={{ mb: 2 }}>
                            <Typography variant="fw500s16lh24c000000">{t("digital_twin:key_metrics")}</Typography>
                            <Grid2 container gap="8px">
                                {(pageMetaInfo.batteryKeyMetrics ?? []).map((keyMetric) => (
                                    <BatteryOverviewKeyMetric keyMetric={keyMetric} value={keyMetricsData[keyMetric.key]} isAlert={false} />
                                ))}
                            </Grid2>
                        </Grid2>
                        {Array.isArray(stringsData) && stringsData.length > 0 && (
                            <BatteryOverviewStringv2 stringsData={stringsData} />
                        )}
                    </Paper>
                </OverviewContainer>
            </Grid2>
        </>
    )
}
